// src/utils/documentCompression.js
import pako from "pako";

/**
 * Compress a document file using gzip
 * @param {File} file - Document file to compress
 * @returns {Promise<Object>} - Compressed document with metadata
 */
export const compressDocument = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);

        // ✅ Store original size for logging
        const originalSize = file.size;

        // Compress using gzip
        const compressed = pako.gzip(uint8Array, { level: 9 }); // Max compression

        // Calculate compression stats
        const compressedSize = compressed.length;
        const compressionRatio = Math.round(
          (1 - compressedSize / originalSize) * 100,
        );

        // ✅ SIMPLE ONE-LINE LOG
        console.log(
          `📄 "${file.name}": ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${compressionRatio}% compression)`,
        );

        // Convert to base64
        const base64 = uint8ArrayToBase64(compressed);

        // Get file extension
        const extension = file.name.split(".").pop().toLowerCase();

        // Get MIME type
        const mimeType = getMimeType(extension);

        // Create data URL
        const dataUrl = `data:${mimeType};base64,${base64}`;

        const result = {
          name: file.name,
          data: dataUrl,
          size: compressedSize,
          originalSize: originalSize,
          type: mimeType,
          extension: extension,
          compressionRatio: compressionRatio,
        };

        resolve(result);
      } catch (error) {
        console.error("Document compression error:", error);
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Decompress a gzip-compressed base64 document
 * @param {string} base64Data - Compressed base64 data
 * @returns {Uint8Array} - Decompressed data
 */
export const decompressDocument = (base64Data) => {
  try {
    // Remove data URL prefix if present
    const base64 = base64Data.includes(",")
      ? base64Data.split(",")[1]
      : base64Data;

    // Decode base64 to Uint8Array
    const compressed = base64ToUint8Array(base64);

    // Decompress using gzip
    const decompressed = pako.ungzip(compressed);

    return decompressed;
  } catch (error) {
    console.error("Document decompression error:", error);
    throw error;
  }
};

/**
 * Convert Uint8Array to base64
 */
const uint8ArrayToBase64 = (uint8Array) => {
  let binary = "";
  const len = uint8Array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
};

/**
 * Convert base64 to Uint8Array
 */
const base64ToUint8Array = (base64) => {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Get MIME type from file extension
 */
const getMimeType = (extension) => {
  const mimeTypes = {
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    txt: "text/plain",
  };

  return mimeTypes[extension] || "application/octet-stream";
};

/**
 * Validate document file type
 * @param {File} file - File to validate
 * @returns {boolean} - True if valid
 */
export const isValidDocumentType = (file) => {
  const validExtensions = [
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "txt",
  ];
  const extension = file.name.split(".").pop().toLowerCase();
  return validExtensions.includes(extension);
};

/**
 * Validate document file size (before compression)
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum size in MB (default 30MB)
 * @returns {Object} - Validation result
 */
export const validateDocumentSize = (file, maxSizeMB = 30) => {
  const maxBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxBytes) {
    return {
      valid: false,
      message: `File size ${formatFileSize(file.size)} exceeds the ${maxSizeMB}MB limit`,
      size: file.size,
      maxSize: maxBytes,
    };
  }

  return {
    valid: true,
    message: "File size is valid",
    size: file.size,
    maxSize: maxBytes,
  };
};

/**
 * Validate and compress document
 * @param {File} file - Document file
 * @param {number} maxSizeMB - Max size before compression
 * @returns {Promise<Object>} - Compressed document
 */
export const validateAndCompressDocument = async (file, maxSizeMB = 30) => {
  // Validate file type
  if (!isValidDocumentType(file)) {
    throw new Error(
      `Invalid file type. Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT`,
    );
  }

  // Validate file size
  const sizeValidation = validateDocumentSize(file, maxSizeMB);
  if (!sizeValidation.valid) {
    throw new Error(sizeValidation.message);
  }

  // Compress document
  const compressed = await compressDocument(file);

  return compressed;
};

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + " " + sizes[i];
};

/**
 * Get file icon name based on extension (for lucide-react)
 * @param {string} extension - File extension
 * @returns {string} - Icon name
 */
export const getDocumentIcon = (extension) => {
  const icons = {
    pdf: "FileText",
    doc: "FileText",
    docx: "FileText",
    xls: "Sheet",
    xlsx: "Sheet",
    ppt: "Presentation",
    pptx: "Presentation",
    txt: "FileType",
  };

  return icons[extension.toLowerCase()] || "File";
};

/**
 * Get file color based on extension
 * @param {string} extension - File extension
 * @returns {string} - Tailwind color class
 */
export const getDocumentColor = (extension) => {
  const colors = {
    pdf: "text-red-600",
    doc: "text-blue-600",
    docx: "text-blue-600",
    xls: "text-green-600",
    xlsx: "text-green-600",
    ppt: "text-orange-600",
    pptx: "text-orange-600",
    txt: "text-gray-600",
  };

  return colors[extension.toLowerCase()] || "text-gray-600";
};

/**
 * Download document from base64
 * @param {string} base64Data - Base64 document data
 * @param {string} filename - Filename for download
 */
export const downloadDocument = (base64Data, filename) => {
  try {
    // Decompress the document
    const decompressed = decompressDocument(base64Data);

    // Get MIME type from filename
    const extension = filename.split(".").pop().toLowerCase();
    const mimeType = getMimeType(extension);

    // Create blob
    const blob = new Blob([decompressed], { type: mimeType });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);

    console.log(`📥 Downloaded: ${filename}`);
  } catch (error) {
    console.error("Download error:", error);
    throw new Error("Failed to download document");
  }
};

//8
